import { useLogger } from "@kensho/lumberjack";
import { use, useCallback, useEffect, useMemo, useReducer } from "react";

import { logout } from "../api/logout";
import { me } from "../api/me";
import { refreshAccessToken } from "../api/refresh-access-token";
import { User } from "../types/types";
import { assertNever } from "../utils/assert-never";
import { SiteAnalyticsContext } from "./site-analytics-context";
import { UserContext, UserContextInterface } from "./user-context";

type UserAction =
  | { type: "logout" }
  | { type: "updateUser"; user: User }
  | { type: "updateAccessToken"; token: string };

interface UserState {
  user?: User;
  _state: UserContextInterface["_state"];
}

function userReducer(state: UserState, action: UserAction): UserState {
  switch (action.type) {
    case "updateUser":
      return { _state: "known", user: action.user };
    case "updateAccessToken":
      if (!state.user) return state;
      return { ...state, user: { ...state.user, token: action.token } };
    case "logout":
      return { _state: "known" };
    default:
      return assertNever(action, "Unknown action type");
  }
}

function getUser(dispatch: (action: UserAction) => void): void {
  me()
    .then((user) => {
      dispatch({ type: "updateUser", user });

      // refresh the access token immediately since user may be returning to the page with
      // session cookie and access token could be expired already
      return refreshAccessToken(user.refreshToken).then(({ accessToken }) => {
        dispatch({ type: "updateAccessToken", token: accessToken });
      });
    })
    .catch(() => dispatch({ type: "logout" }));
}

export function UserProvider(props: { children: React.ReactNode }) {
  const { children } = props;
  const [{ user, _state }, dispatch] = useReducer(userReducer, {
    _state: "unknown",
  });
  const log = useLogger();

  const analytics = use(SiteAnalyticsContext);

  const triggerUpstreamLogout = useCallback((): void => {
    logout().catch((error) => {
      log.error(error);
      // user should already be unset, but reset the state on an error just in case
      dispatch({ type: "logout" });
    });
  }, [log, dispatch]);

  // attempt to retrieve the user on mount
  useEffect(() => {
    getUser(dispatch);
  }, []);

  useEffect(() => {
    if (user) {
      let userType = "external";
      if (user.email.endsWith("spglobal.com")) userType = "sp_global";
      if (user.email.endsWith("kensho.com")) userType = "kensho";

      analytics.setSessionContext("user_properties", "user_type", userType);
    }
  }, [analytics, user]);

  // refresh access token every 15 minutes if user hasn't logged out to avoid access token expiry
  useEffect(() => {
    let isCurrent = true;
    const timeoutId = window.setTimeout(
      () => {
        if (!user?.refreshToken) return;
        refreshAccessToken(user.refreshToken).then(
          ({ accessToken }) =>
            isCurrent &&
            dispatch({
              type: "updateAccessToken",
              token: accessToken,
            }),
        );
      },
      15 * 60 * 1000, // 15 minutes
    );

    return () => {
      window.clearTimeout(timeoutId);
      isCurrent = false;
    };
  }, [user]);

  const userContextValue = useMemo(
    () => ({
      user,
      _state,
      refreshToken() {
        if (!user?.refreshToken) return;
        refreshAccessToken(user.refreshToken).then(({ accessToken }) =>
          dispatch({
            type: "updateAccessToken",
            token: accessToken,
          }),
        );
      },
      logout() {
        dispatch({ type: "logout" });
        triggerUpstreamLogout();
      },
    }),
    [_state, user, triggerUpstreamLogout],
  );

  return <UserContext value={userContextValue}>{children}</UserContext>;
}
